/**
 * SDK 硬件缓存层 - InflightRegistry (Phase 1)
 *
 * "进行中请求登记表": 同一个 `key` 的并发请求只发一次硬件交互,后续调用复用同一个
 * Promise。本类**不存任何数据**,只登记"进行中的 Promise",请求结束(成功或失败)
 * 后立刻从登记表移除。
 *
 * 详见 design.md `Components §1`。
 *
 * --- 关键约束 (与 design / requirements 一一对齐) ---
 *
 * 1. **失败也会清理登记** (Requirement 1.4):
 *    无论 `factory` 返回的 Promise 是 resolve 还是 reject,登记项都必须移除。
 *    否则一次失败会让后续所有同 key 请求永远拿到失败的 Promise。
 *    本实现使用 `try { ... } finally { this.entries.delete(key) }` 保证这一点。
 *
 * 2. **不持久化结果** (与 Phase 2 `HardwareCache` 严格分离):
 *    成功也不缓存结果。"缓存结果"是 Phase 2 的职责,这里只解决"并发去重"。
 *
 * 3. **复用同一个 Promise** (Requirement 1.1, 1.2, 1.3):
 *    所有并发调用方收到的是**同一个 Promise 实例**,因此最终
 *    resolve 的值与 reject 的错误对所有调用方完全一致。
 *
 * 4. **`factory` 仅在登记时调用一次** (Requirement 1.1):
 *    若 key 已在登记表中,`factory` 不会被调用,直接返回已登记的 Promise。
 *
 * 5. **key 与 Phase 2 共用**:
 *    `key` 由 `cache/CacheKey.ts` 的 `makeKey(address, length)` 生成,本类不做 key 解析。
 *
 * 6. **独立于 `HardwareCache.disable()`** (Requirement 1.6):
 *    去重能力不依赖缓存启用状态;`InflightRegistry` 自身不感知 `HardwareCache`。
 *
 * --- 错误处理 ---
 *
 * 见 design `Error Handling §1` (读取失败):
 * - factory 同步抛异常 → 登记项不写入(直接由本方法重抛,见下文实现)
 * - factory 返回 Promise 后 reject → 登记项 finally 清理,异常透传给所有等待方
 *
 * @packageDocumentation
 */

import type { CacheKey } from './types'

/**
 * 进行中请求登记表。
 *
 * 同一个 `key` 的并发调用复用同一个 Promise,完成(成功或失败)后立即从登记表移除。
 *
 * 实例选择:每个 `BaseDevice` 持有一个 `InflightRegistry` 实例(Phase 1 任务 2.2 接入)。
 * 本类自身不感知 `BaseDevice`,可被任意上层组件复用。
 */
export class InflightRegistry {
  /**
   * 登记表本体。`Map<CacheKey, Promise<unknown>>`。
   *
   * 使用 `unknown` 是因为 `run<T>` 的泛型仅在调用点有效,不同 key 可承载不同类型的 Promise。
   * 强制类型转换发生在 `run<T>` 的返回值处,这是一个受控的内部实现细节。
   */
  private readonly entries = new Map<CacheKey, Promise<unknown>>()

  /**
   * 同 key 的请求复用同一个 Promise。
   *
   * - 若 `key` 已在登记表中,直接返回登记的 Promise (`factory` **不会被调用**)
   * - 否则调用 `factory()`,把返回的 Promise 登记起来,完成(成功或失败)后自动清理
   *
   * 失败路径(Promise reject)同样会清理登记项(Requirement 1.4),
   * 避免一次失败让后续同 key 请求永远拿到失败的 Promise。
   *
   * @typeParam T factory 返回 Promise 的 resolve 类型
   * @param key 缓存键,通常由 `makeKey(address, length)` 生成
   * @param factory 真实硬件读取的工厂函数。仅在登记表无对应项时被调用一次
   * @returns 与所有同 key 并发调用方共享的同一个 Promise
   */
  run<T>(key: CacheKey, factory: () => Promise<T>): Promise<T> {
    // 已登记: 直接复用,factory 不调用
    const existing = this.entries.get(key)
    if (existing !== undefined) {
      return existing as Promise<T>
    }

    // 未登记: 调用 factory 并登记
    // 注意: 如果 factory() 同步抛异常 (违反约定,但需防御),不会污染登记表 ——
    // 此时 entries 不会写入,异常直接抛给调用方。
    const promise = factory()

    // 用 .then(undefined, () => {}) 包一层"清理钩子",同时保留原 promise 的成功/失败语义
    // 关键: 我们登记并返回的是 *原始* promise (而不是 .then 后的 promise),
    // 这样所有等待方都能拿到与 factory 一致的 resolve 值/reject 错误。
    const cleanup = (): void => {
      // 仅当登记项仍指向当前 promise 时才删除,避免误删后续重新登记的项
      // (理论上不会出现,因为 cleanup 在登记项消失之前必先执行;但加一层防御无伤大雅)
      if (this.entries.get(key) === promise) {
        this.entries.delete(key)
      }
    }
    // 使用 .then(cleanup, cleanup) 等价于 finally,但 finally 在某些目标(ES2017 以下)
    // 需要 polyfill。这里 monorepo target 是 ESNext,可以直接用 finally。
    promise.then(cleanup, cleanup)

    this.entries.set(key, promise)
    return promise
  }

  /**
   * 强制丢弃某个 in-flight 登记。
   *
   * 一般业务侧不会用到,留作调试逃生口:例如某条请求被外部 abort、或登记表
   * 因未知原因卡住时,可调此方法手动清理。
   *
   * **注意**:`drop` 不会取消已经在 `factory` 中执行的 Promise;只是让登记表不再持有
   * 它的引用。后续同 key 调用会重新执行 `factory`,产生一次新的硬件交互。
   *
   * 若 `key` 不在登记表中,本方法为 no-op,不抛错。
   *
   * @param key 要丢弃的缓存键
   */
  drop(key: CacheKey): void {
    this.entries.delete(key)
  }

  /**
   * 当前进行中的请求数量。
   *
   * 用途:调试与可观测性。例如在测试中断言并发去重生效后,某一时刻 `size()` 仅为 1。
   *
   * @returns 登记表中尚未结束的 Promise 数量
   */
  size(): number {
    return this.entries.size
  }
}
